<template>
  <div v-if="!hasPermission('List Plan')">Você não tem permissão para acessar esta página!</div>
  <div v-else>
    <Toolbar>
      <template #actions>
        <router-link to="/plans" class="btn btn-sm btn-light-primary me-2">
          <i class="bi bi-arrow-left me-1"></i> Planos
        </router-link>
      </template>
    </Toolbar>

    <div class="container-xxl">
      <div class="card">
        <div class="card-header">
          <div class="card-title">
            <div class="d-flex align-items-center position-relative">
              <span class="svg-icon svg-icon-1 position-absolute ms-6">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <rect opacity="0.5" x="17.0365" y="15.1223" width="8.15546" height="2" rx="1"
                        transform="rotate(45 17.0365 15.1223)" fill="black"></rect>
                  <path d="M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z"
                        fill="black"></path>
                </svg>
              </span>
              <input type="search" v-model="search" @input="loadCompanies"
                     class="form-control form-control-solid w-350px ps-14"
                     placeholder="Nome ou CNPJ...">
            </div>
          </div>
          <div class="card-toolbar d-flex align-items-center gap-2">
            <div class="btn-group btn-group-sm" role="group">
              <button type="button" class="btn"
                      :class="viewMode === 'card' ? 'btn-primary' : 'btn-light'"
                      @click="viewMode = 'card'" title="Visualizar em cards">
                <i class="bi bi-grid-3x3-gap"></i>
              </button>
              <button type="button" class="btn"
                      :class="viewMode === 'table' ? 'btn-primary' : 'btn-light'"
                      @click="viewMode = 'table'" title="Visualizar em tabela">
                <i class="bi bi-list-ul"></i>
              </button>
            </div>
            <button class="btn btn-primary" @click="newCompany">
              Nova Empresa
            </button>
          </div>
        </div>
        <div class="card-body py-4">
          <div v-if="viewMode === 'card'" class="row g-3 mb-3">
            <div v-for="company in companies.data" :key="company.id_company" class="col-12 col-md-6 col-xl-4">
              <div class="card card-bordered h-100">
                <div class="card-body">
                  <div class="d-flex justify-content-between align-items-start mb-2">
                    <div class="fw-bold text-dark">{{ company.company_name }}</div>
                    <span :class="company.company_active ? 'badge badge-success' : 'badge badge-danger'">
                      {{ company.company_active ? 'Ativo' : 'Inativo' }}
                    </span>
                  </div>
                  <p class="text-muted small mb-1">{{ company.formatted_cnpj }}</p>
                  <div class="fs-7 mb-2">
                    <span v-if="company.company_email" class="d-block">{{ company.company_email }}</span>
                    <span v-if="company.company_phone" class="d-block">{{ company.company_phone }}</span>
                  </div>
                  <div class="mb-3">
                    <span v-if="contractAlerts[company.id_company] === 'loading'" class="text-muted small">
                      <i class="spinner-border spinner-border-sm"></i>
                    </span>
                    <span v-else-if="contractAlerts[company.id_company] === 'expired'"
                          class="badge badge-warning"
                          title="Existe contrato ativo vencido">Vencido</span>
                    <span v-else-if="contractAlerts[company.id_company] === 'expiring'"
                          class="badge badge-info"
                          title="Existe contrato ativo a vencer nos próximos 30 dias">A vencer</span>
                    <span v-else class="text-muted small">—</span>
                  </div>
                  <div class="d-flex flex-wrap gap-1">
                    <button class="btn btn-sm btn-icon btn-bg-light btn-active-color-primary border border-gray-400"
                            type="button" @click="showSubscribers(company)" title="Funcionários">
                      <i class="bi bi-people"></i>
                    </button>
                    <button class="btn btn-sm btn-icon btn-bg-light btn-active-color-primary border border-gray-400"
                            type="button" @click="showBillingClosing(company)" title="Fechamento">
                      <i class="bi bi-calendar-check"></i>
                    </button>
                    <button type="button"
                            class="btn btn-sm btn-icon btn-bg-light btn-active-color-primary border border-gray-400"
                            :class="{ show: dropdownCompany && dropdownCompany.id_company === company.id_company }"
                            @click.stop="toggleActionsDropdown(company, $event)"
                            title="Mais ações">
                      <i class="bi bi-three-dots-vertical"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-if="viewMode === 'card' && companies.data?.length === 0" class="text-center text-muted py-5">
            Nenhuma empresa encontrada.
          </div>
          <div v-if="viewMode === 'table'" class="table-responsive">
            <table class="table table-hover table-row-bordered">
              <thead>
              <tr class="fw-bold fs-6 text-muted">
                <th>Empresa</th>
                <th>CNPJ</th>
                <th>E-mail</th>
                <th>Telefone</th>
                <th class="text-center">Status</th>
                <th class="text-center">Contrato</th>
                <th class="text-center">Ações</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="company in companies.data" :key="company.id_company">
                <td>{{ company.company_name }}</td>
                <td>{{ company.formatted_cnpj }}</td>
                <td>{{ company.company_email }}</td>
                <td>{{ company.company_phone }}</td>
                <td class="text-center">
                  <span :class="company.company_active ? 'badge badge-success' : 'badge badge-danger'">
                    {{ company.company_active ? 'Ativo' : 'Inativo' }}
                  </span>
                </td>
                <td class="text-center">
                  <span v-if="contractAlerts[company.id_company] === 'loading'" class="text-muted small">
                    <i class="spinner-border spinner-border-sm"></i>
                  </span>
                  <span v-else-if="contractAlerts[company.id_company] === 'expired'"
                        class="badge badge-warning"
                        title="Existe contrato ativo vencido">Vencido</span>
                  <span v-else-if="contractAlerts[company.id_company] === 'expiring'"
                        class="badge badge-info"
                        title="Existe contrato ativo a vencer nos próximos 30 dias">A vencer</span>
                  <span v-else class="text-muted">—</span>
                </td>
                <td class="text-center" style="min-width: 180px;">
                  <!-- Botões principais -->
                  <button class="btn btn-sm btn-icon btn-bg-light btn-active-color-primary me-1 border border-gray-400"
                          type="button" @click="showSubscribers(company)" title="Funcionários">
                    <i class="bi bi-people"></i>
                  </button>
                  <button class="btn btn-sm btn-icon btn-bg-light btn-active-color-primary me-1 border border-gray-400"
                          type="button" @click="showBillingClosing(company)" title="Fechamento">
                    <i class="bi bi-calendar-check"></i>
                  </button>
                  
                  <!-- Dropdown com demais ações (renderizado no body para não causar scroll na tabela) -->
                  <div class="btn-group">
                    <button type="button" 
                            class="btn btn-sm btn-icon btn-bg-light btn-active-color-primary border border-gray-400"
                            :class="{ show: dropdownCompany && dropdownCompany.id_company === company.id_company }"
                            @click.stop="toggleActionsDropdown(company, $event)"
                            title="Mais ações">
                      <i class="bi bi-three-dots-vertical"></i>
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="!companies.data?.length">
                <td colspan="8" class="text-center text-muted py-5">Nenhuma empresa encontrada.</td>
              </tr>
              </tbody>
            </table>
          </div>
          <Pagination v-if="companies.data?.length > 0" :data="companies" />
        </div>
      </div>

      <!-- Menu dropdown renderizado no body (fora da tabela) -->
      <Teleport to="body">
        <div v-if="dropdownCompany"
             class="dropdown-menu show"
             :style="dropdownStyle"
             @click.stop
             ref="actionsDropdownMenu">
          <a class="dropdown-item" href="#" @click.prevent="runAction('edit')">
            <i class="bi bi-pen me-2"></i>Editar empresa
          </a>
          <a class="dropdown-item" href="#" @click.prevent="runAction('charges')">
            <i class="bi bi-cash-coin me-2"></i>Visualizar cobranças
          </a>
          <a class="dropdown-item" href="#" @click.prevent="runAction('contract')">
            <i class="bi bi-file-earmark-arrow-down me-2"></i>Contratos
          </a>
          <hr class="dropdown-divider">
          <a class="dropdown-item text-danger" href="#" @click.prevent="runAction('delete')">
            <i class="bi bi-trash me-2"></i>Excluir empresa
          </a>
        </div>
        <div v-if="dropdownCompany" class="position-fixed top-0 start-0 w-100 h-100" style="z-index: 1040;" @click="closeActionsDropdown" aria-hidden="true"></div>
      </Teleport>

      <CompanyModal
        :company="selectedCompany"
        @onClose="closeCompanyModal"
        @companySaved="loadCompanies"
      />

      <CompanySubscribersModal
        :company="selectedCompanyForSubscribers"
        :expand-form-on-open="expandFormOnOpen"
        @onClose="closeSubscribersModal"
        @opened="expandFormOnOpen = false"
        @subscriptionSaved="loadCompanies"
      />

      <CompanyContractsModal
        :company="selectedCompanyForContracts"
        @onClose="closeContractsModal"
      />

      <CompanyBillingClosingModal
        :company="selectedCompanyForBillingClosing"
        @onClose="selectedCompanyForBillingClosing = null"
        @closed="onBillingClosed"
      />

      <CompanyChargesModal
        :company="selectedCompanyForCharges"
        @onClose="selectedCompanyForCharges = null"
      />
    </div>
  </div>
</template>

<script>
import Pagination from "@/components/Pagination";
import Permission from "@/helpers/permission";
import Toolbar from "@/components/layouts/auth/Toolbar.vue";
import Modal from "@/helpers/modal";
import Loader from "@/helpers/loader";
import Alert from "@/helpers/alert";
import Download from "@/helpers/download";
import CompanyModal from "@/views/plan/company/CompanyModal.vue";
import CompanySubscribersModal from "@/views/plan/company/CompanySubscribersModal.vue";
import CompanyContractsModal from "@/views/plan/company/CompanyContractsModal.vue";
import CompanyBillingClosingModal from "@/views/plan/company/CompanyBillingClosingModal.vue";
import CompanyChargesModal from "@/views/plan/company/CompanyChargesModal.vue";
import { CompanyService } from "@/service/companyService";

export default {
  name: "Company",
  components: {
    Pagination,
    Toolbar,
    CompanyModal,
    CompanySubscribersModal,
    CompanyContractsModal,
    CompanyBillingClosingModal,
    CompanyChargesModal,
  },
  data() {
    return {
      search: '',
      companies: [],
      viewMode: 'table',
      contractAlerts: {},
      selectedCompany: null,
      selectedCompanyForSubscribers: null,
      selectedCompanyForContracts: null,
      selectedCompanyForBillingClosing: null,
      selectedCompanyForCharges: null,
      expandFormOnOpen: false,
      companyService: CompanyService.new(),
      dropdownCompany: null,
      dropdownPosition: { top: 0, left: 0 },
    }
  },
  computed: {
    place() {
      return this.$store.state.auth.place;
    },
    dropdownStyle() {
      if (!this.dropdownPosition.top && !this.dropdownPosition.left) return {};
      return {
        position: 'fixed',
        top: `${this.dropdownPosition.top}px`,
        left: `${this.dropdownPosition.left}px`,
        zIndex: 1050,
        minWidth: '11rem',
      };
    },
  },
  mounted() {
    this.loadCompanies();
  },
  beforeUnmount() {
    document.removeEventListener('click', this.closeActionsDropdown);
  },
  watch: {
    '$route.query.page'() {
      this.loadCompanies();
    },
  },
  methods: {
    hasPermission(permission) {
      return Permission.has(permission);
    },
    loadCompanies() {
      this.companyService.list(this.place.id_place, {
        page: this.$route.query.page,
        search: this.search,
        per_page: 15,
      }).then((response) => {
        this.companies = response.data;
        this.loadContractAlerts();
      });
    },
    loadContractAlerts() {
      const list = this.companies?.data || [];
      this.contractAlerts = {};
      list.forEach(c => { this.contractAlerts[c.id_company] = 'loading'; });
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const in30Days = new Date(today);
      in30Days.setDate(in30Days.getDate() + 30);
      Promise.all(list.map(company =>
        this.companyService.contracts(this.place.id_place, company.id_company, { per_page: 100 })
          .then((res) => {
            const contracts = res.data?.data || res.data || [];
            let status = null;
            for (const c of contracts) {
              if (c.contract_status !== 'active') continue;
              const validUntil = c.valid_until ? new Date(c.valid_until) : null;
              if (!validUntil) continue;
              validUntil.setHours(0, 0, 0, 0);
              const isExpired = c.is_expired === true || validUntil < today;
              if (isExpired) {
                status = 'expired';
                break;
              }
              if (validUntil <= in30Days) {
                status = status || 'expiring';
              }
            }
            this.contractAlerts[company.id_company] = status;
          })
          .catch(() => {
            this.contractAlerts[company.id_company] = null;
          })
      ));
    },
    newCompany() {
      this.selectedCompany = null;
      Modal.toggle('#companyModal');
    },
    editCompany(company) {
      this.selectedCompany = company;
      Modal.toggle('#companyModal');
    },
    closeCompanyModal() {
      this.selectedCompany = null;
    },
    closeSubscribersModal() {
      this.selectedCompanyForSubscribers = null;
      this.expandFormOnOpen = false;
    },
    showSubscribers(company) {
      this.selectedCompanyForSubscribers = company;
      this.expandFormOnOpen = false;
      this.$nextTick(() => Modal.toggle('#companySubscribersModal'));
    },
    showBillingClosing(company) {
      this.selectedCompanyForBillingClosing = company;
      this.$nextTick(() => Modal.toggle('#companyBillingClosingModal'));
    },
    onBillingClosed() {
      if (this.selectedCompanyForBillingClosing) {
        this.selectedCompanyForCharges = this.selectedCompanyForBillingClosing;
        this.$nextTick(() => Modal.toggle('#companyChargesModal'));
      }
    },
    showCharges(company) {
      this.selectedCompanyForCharges = company;
      this.$nextTick(() => {
        Modal.toggle('#companyChargesModal');
      });
    },
    showContracts(company) {
      this.selectedCompanyForContracts = company;
      this.$nextTick(() => {
        Modal.toggle('#companyContractsModal');
      });
    },
    closeContractsModal() {
      this.selectedCompanyForContracts = null;
      this.loadCompanies();
    },
    deleteCompany(company) {
      Swal.fire({
        title: 'Excluir empresa',
        text: `Tem certeza que deseja excluir a empresa "${company.company_name}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, excluir',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
      }).then((result) => {
        if (result.isConfirmed) {
          Loader.show('Excluindo empresa...');
          this.companyService.destroy(this.place.id_place, company.id_company)
            .then(() => {
              Loader.close();
              Alert.success('Empresa excluída com sucesso.');
              this.loadCompanies();
            })
            .catch((error) => {
              Loader.close();
              Alert.error(error.response?.data?.message || 'Erro ao excluir empresa.');
            });
        }
      });
    },
    toggleActionsDropdown(company, event) {
      if (this.dropdownCompany && this.dropdownCompany.id_company === company.id_company) {
        this.closeActionsDropdown();
        return;
      }
      const btn = event.currentTarget;
      const rect = btn.getBoundingClientRect();
      const menuWidth = 176;
      this.dropdownCompany = company;
      this.dropdownPosition = {
        top: rect.bottom + 4,
        left: Math.max(8, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8)),
      };
      this.$nextTick(() => {
        document.addEventListener('click', this.closeActionsDropdown);
      });
    },
    closeActionsDropdown() {
      this.dropdownCompany = null;
      document.removeEventListener('click', this.closeActionsDropdown);
    },
    runAction(action) {
      const company = this.dropdownCompany;
      if (!company) return;
      this.closeActionsDropdown();
      if (action === 'edit') this.editCompany(company);
      else if (action === 'charges') this.showCharges(company);
      else if (action === 'contract') this.showContracts(company);
      else if (action === 'delete') this.deleteCompany(company);
    },
  }
}
</script>
